import {
  Card,
  Space,
  Typography,
  Tabs,
  Tag,
  Spin,
  Alert,
  Form,
  Input,
  Button,
  message,
  Table,
  Select,
  Tooltip,
} from 'antd';
import Layout from 'components/Layout';
import React, { useCallback, useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { fetchOrganizationDetails } from 'api/organizations';
import { AdminContext } from 'constants/contexts';
import http from 'utils/http';
import { captureException } from 'utils/errors';
import {
  normalizeOrganizationFromDetails,
  persistOrganizationSelection,
  shouldSkipDeepLinkOrganizationSwitch,
} from 'utils/organization';
import POSIntegrations from './POSIntegrations';
import EzCaterIntegration from './EzCaterIntegration';
import { InfoCircleOutlined } from '@ant-design/icons';
import {
  InfoTable,
  SectionTitle,
  formItemStyle,
  sectionCardProps,
  sectionStackGap,
  settingsContentMaxWidth,
} from './settingsShared';
import './settingsStyles.css';

const SETTINGS_TABS = new Set(['delivery-settings', 'email', 'integrations']);

function tabFromHash(hash) {
  const key = (hash || '').replace(/^#/, '').toLowerCase();
  if (key === 'platform-integrations') return 'delivery-settings';
  if (SETTINGS_TABS.has(key)) return key;
  return 'delivery-settings';
}

const { Text } = Typography;

const FULFLLD_SUPPORT_HELP =
  'FULFLLD is not configured for this organization. Contact support to enable it.';

function maskedCredential(last4) {
  if (!last4) return 'Not Configured';
  return (
    <Text>
      Configured
      {' '}
      ******
      <Text strong style={{ fontWeight: 700 }}>{last4}</Text>
    </Text>
  );
}

function expediteConnection(org) {
  const dedicated = Boolean(org.expedite_api_token_last4 || org.expedite_api_secret_last4);
  if (dedicated) {
    return {
      tag: <Tag color="success">Connected - via Expedite dedicated account</Tag>,
      detail: 'This organization uses its own Expedite credentials.',
    };
  }
  // Match platform Dispatchers tab: shared = org has an Expedite dispatcher FK.
  // Do not treat global env URL/token alone as "connected".
  if (org.dispatcher) {
    return {
      tag: <Tag color="success">Connected - via Expedite shared account</Tag>,
      detail: 'Using the shared / platform Expedite dispatcher credentials.',
    };
  }
  return {
    tag: <Tag>Not configured</Tag>,
    detail: 'No Expedite connection is available for this organization.',
  };
}

function fulflldConnection(org) {
  const hasCreds = Boolean(org.fulflld_app_id_last4 || org.fulflld_app_secret_last4);
  if (!hasCreds) {
    // Match platform Dispatchers tab: empty credentials → "Not configured".
    return {
      tag: <Tag color={org.fulflld_enabled ? 'warning' : undefined}>Not configured</Tag>,
      detail: org.fulflld_enabled
        ? FULFLLD_SUPPORT_HELP
        : 'FULFLLD is not configured for this organization. Contact support to enable it.',
      available: false,
    };
  }
  if (!org.fulflld_enabled) {
    return {
      tag: <Tag>Disabled</Tag>,
      detail: 'FULFLLD is off for this organization. Contact support to enable it.',
      available: false,
    };
  }
  if (org.fulflld_token_fetch_failed) {
    return {
      tag: <Tag color="error">Credentials invalid</Tag>,
      detail: 'Stored FULFLLD credentials could not obtain a token. Contact support.',
      available: false,
    };
  }
  if (org.has_valid_fulflld_token) {
    return {
      tag: <Tag color="success">Connected</Tag>,
      detail: 'FULFLLD credentials are configured and a bearer token is available.',
      available: true,
    };
  }
  return {
    tag: <Tag color="warning">Configured</Tag>,
    detail: 'Credentials are saved. Token will refresh on the next FULFLLD request.',
    available: Boolean(org.fulflld_dispatcher),
  };
}

function emailSendingConnection(emailSending) {
  if (!emailSending) {
    return {
      tag: <Tag>Not configured</Tag>,
      detail: 'No email sending identity is configured for this organization.',
      statusLabel: 'Not configured',
      fromEmail: null,
    };
  }
  if (emailSending.using_platform_default) {
    return {
      tag: <Tag>Platform default</Tag>,
      detail: 'Emails are sent from the platform default address (noreply@nibble.io).',
      statusLabel: 'Using platform default (noreply@nibble.io)',
      fromEmail: 'noreply@nibble.io',
    };
  }
  const identity = emailSending.identity;
  if (!identity) {
    return {
      tag: <Tag>Not configured</Tag>,
      detail: 'No email sending identity is configured for this organization.',
      statusLabel: 'Not configured',
      fromEmail: null,
    };
  }
  const verified = identity.verification_status === 'verified';
  return {
    tag: (
      <Tag color={verified ? 'success' : 'warning'}>
        {identity.verification_status || 'pending'}
      </Tag>
    ),
    detail: verified
      ? 'Your domain is verified. Outbound email uses your brand from address.'
      : 'Domain authentication is set up. Add the DNS records below, then verify.',
    statusLabel: verified ? 'Verified' : (identity.verification_status || 'Pending'),
    fromEmail: identity.from_email,
    fromName: identity.from_name,
    sendingDomain: identity.sending_domain,
  };
}

function OrganizationSettings() {
  const { organizationId, organization: selectedOrganization, setOrganization: setSelectedOrganization } = useContext(AdminContext);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [error, setError] = useState(null);
  const [deliveryForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [emailSending, setEmailSending] = useState(null);
  const [emailSendingLoading, setEmailSendingLoading] = useState(false);
  const [emailSendingSaving, setEmailSendingSaving] = useState(false);
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => tabFromHash(window.location.hash));

  const syncTabToUrl = useCallback((key) => {
    const nextHash = `#${key}`;
    if (location.hash === nextHash) return;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: key,
      },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const fromUrl = tabFromHash(location.hash);
    setActiveTab(fromUrl);
    // Ensure a tab hash is always present (reload / first visit).
    if (!location.hash || tabFromHash(location.hash) !== location.hash.replace(/^#/, '')) {
      syncTabToUrl(fromUrl);
    }
  }, [location.hash, syncTabToUrl]);

  const onTabChange = (key) => {
    setActiveTab(key);
    syncTabToUrl(key);
  };

  const fetchOrganization = () => {
    if (organizationId) {
      setLoading(true);
      http.get(`v1/organizations/${organizationId}/`)
        .then((res) => {
          const orgData = res.data.data || res.data;
          setOrganization(orgData);
          deliveryForm.setFieldsValue({
            default_delivery_type: orgData.default_delivery_type,
          });
          setError(null);
        })
        .catch((e) => {
          console.error('Failed to load organization:', e);
          captureException(e);
          if (e.response?.status === 401) {
            setError('Your session expired. Log in to Nibble admin, then open Settings → Delivery Settings.');
          } else {
            setError('Failed to load organization details');
          }
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchOrganization();
  }, [organizationId]);

  useEffect(() => {
    const requestedOrgId = searchParams.get('organizationId');
    if (!requestedOrgId || !setSelectedOrganization) return;

    if (shouldSkipDeepLinkOrganizationSwitch({
      requestedOrgId,
      organizationId,
      selectedOrganization,
    })) {
      return;
    }

    fetchOrganizationDetails(requestedOrgId)
      .then((res) => {
        const org = normalizeOrganizationFromDetails(res);
        if (!org) return;

        persistOrganizationSelection(org);
        setSelectedOrganization(org);
      })
      .catch(captureException);
  }, [searchParams, organizationId, selectedOrganization, setSelectedOrganization]);

  useEffect(() => {
    if (organizationId) {
      fetchEmailSending();
    }
  }, [organizationId]);

  const fetchEmailSending = () => {
    if (!organizationId) return;
    setEmailSendingLoading(true);
    http.get(`v1/organizations/${organizationId}/email-sending/`)
      .then((res) => {
        const data = res.data?.data || res.data;
        setEmailSending(data);
        if (data?.identity) {
          emailForm.setFieldsValue({
            sending_domain: data.identity.sending_domain,
            from_email: data.identity.from_email,
            from_name: data.identity.from_name,
          });
        }
      })
      .catch((e) => {
        console.error('Failed to load email sending:', e);
        captureException(e);
      })
      .finally(() => setEmailSendingLoading(false));
  };

  const handleSaveEmailSending = (values) => {
    setEmailSendingSaving(true);
    http.post(`v1/organizations/${organizationId}/email-sending/`, values)
      .then((res) => {
        const data = res.data?.data || res.data;
        setEmailSending({ ...data, using_platform_default: false });
        message.success('Domain authentication created. Add the DNS records below to verify.');
        fetchEmailSending();
      })
      .catch((e) => {
        captureException(e);
        message.error(e.response?.data?.error || 'Failed to create domain authentication');
      })
      .finally(() => setEmailSendingSaving(false));
  };

  const handleVerifyEmailSending = () => {
    setEmailVerifyLoading(true);
    http.post(`v1/organizations/${organizationId}/email-sending/verify/`)
      .then((res) => {
        const data = res.data?.data || res.data;
        if (data?.is_verified) {
          message.success('Domain verified successfully!');
        } else {
          message.info('Not yet verified. DNS propagation usually takes under 1 hour (up to 48 hours).');
        }
        fetchEmailSending();
      })
      .catch((e) => {
        captureException(e);
        const apiError = e.response?.data?.error;
        message.error(apiError || 'Verification failed. DNS propagation usually takes under 1 hour (up to 48 hours).');
      })
      .finally(() => setEmailVerifyLoading(false));
  };

  const handleSaveDeliverySettings = (values) => {
    setSaving(true);
    http.patch(`v1/organizations/${organizationId}/`, {
      default_delivery_type: values.default_delivery_type,
    })
      .then((res) => {
        message.success('Delivery settings saved successfully');
        const orgData = res.data.data || res.data;
        setOrganization(orgData);
        fetchOrganization();
      })
      .catch((e) => {
        captureException(e);
        const apiError = e.response?.data?.error || 'Failed to save settings';
        message.error(apiError);
      })
      .finally(() => setSaving(false));
  };

  const fulflld = organization ? fulflldConnection(organization) : null;
  const expedite = organization ? expediteConnection(organization) : null;
  const emailConn = emailSendingConnection(emailSending);
  const fulflldAvailable = Boolean(
    organization
    && organization.fulflld_enabled
    && organization.fulflld_dispatcher
    && !organization.fulflld_token_fetch_failed
    && (organization.has_valid_fulflld_token || organization.fulflld_app_id_last4),
  );

  const items = [
    {
      key: 'delivery-settings',
      label: 'Delivery Settings',
      children: (
        <Space direction="vertical" size={sectionStackGap} style={{ width: '100%', maxWidth: settingsContentMaxWidth }}>
          <div>
            <Text style={{ color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.5 }}>
              Set default delivery routing and review Expedite / FULFLLD connection details.
              Dispatcher credentials are managed by Nibble support and cannot be edited here.
            </Text>
          </div>

          {loading && <Spin />}

          {error && <Alert message={error} type="error" />}

          {!loading && !error && organization && (
            <>
              <Card {...sectionCardProps} title="Set default dispatcher">
                <Form
                  className="org-settings-form"
                  form={deliveryForm}
                  layout="vertical"
                  onFinish={handleSaveDeliverySettings}
                  style={{ maxWidth: 480 }}
                >
                  <Form.Item
                    label={(
                      <Space size={4}>
                        <span>Select default delivery routing</span>
                        <Tooltip
                          title={(
                            <span>
                              Sets the default delivery routing for new delivery orders.
                              <br />
                              <strong>Self Delivery</strong>
                              : restaurant handles delivery; no dispatcher job is created by default.
                              <br />
                              <strong>Expedite-dispatch</strong>
                              : uses Expedite to dispatch drivers for delivery orders.
                              <br />
                              <strong>FULFLLD</strong>
                              : sends delivery orders to FULFLLD; this option is disabled unless FULFLLD is enabled and configured for the organization.
                            </span>
                          )}
                        >
                          <InfoCircleOutlined style={{ color: '#722ED1' }} />
                        </Tooltip>
                      </Space>
                    )}
                    name="default_delivery_type"
                    style={formItemStyle}
                    extra={!fulflldAvailable ? FULFLLD_SUPPORT_HELP : null}
                  >
                    <Select>
                      <Select.Option value="none">
                        No default (manual choice)
                      </Select.Option>
                      <Select.Option value="self_delivery">
                        Self Delivery
                      </Select.Option>
                      <Select.Option value="omni_dispatch">
                        Expedite-dispatch
                      </Select.Option>
                      <Select.Option
                        value="fulflld"
                        disabled={!fulflldAvailable}
                        title={!fulflldAvailable ? FULFLLD_SUPPORT_HELP : undefined}
                      >
                        {fulflldAvailable ? 'FULFLLD' : 'FULFLLD (contact support to enable)'}
                      </Select.Option>
                    </Select>
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={saving}>
                      Save Delivery Settings
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              <Card
                {...sectionCardProps}
                title={<SectionTitle title="Expedite" tag={expedite.tag} />}
              >
                <Text style={{
                  color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
                }}
                >
                  {expedite.detail}
                </Text>
                <InfoTable
                  rows={[
                    {
                      label: 'Client name',
                      value: organization.expedite_client_name || organization.name || '—',
                    },
                    {
                      label: 'API URL',
                      value: organization.effective_expedite_api_url
                        ? <Text code>{organization.effective_expedite_api_url}</Text>
                        : '—',
                    },
                    {
                      label: 'API token',
                      value: organization.expedite_api_token_last4
                        ? maskedCredential(organization.expedite_api_token_last4)
                        : (organization.effective_expedite_api_token
                          ? <Text code>{organization.effective_expedite_api_token}</Text>
                          : 'Not Configured'),
                    },
                    {
                      label: 'API secret',
                      value: organization.expedite_api_secret_last4
                        ? maskedCredential(organization.expedite_api_secret_last4)
                        : (organization.effective_expedite_api_secret
                          ? <Text code>{organization.effective_expedite_api_secret}</Text>
                          : 'Not Configured'),
                    },
                  ]}
                />
              </Card>

              <Card
                {...sectionCardProps}
                title={<SectionTitle title="FULFLLD" tag={fulflld.tag} />}
              >
                <Text style={{
                  color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
                }}
                >
                  {fulflld.detail}
                </Text>
                <InfoTable
                  rows={[
                    {
                      label: 'API URL',
                      value: organization.effective_fulflld_api_url
                        ? <Text code>{organization.effective_fulflld_api_url}</Text>
                        : '—',
                    },
                    {
                      label: 'App ID',
                      value: maskedCredential(organization.fulflld_app_id_last4),
                    },
                    {
                      label: 'App secret',
                      value: maskedCredential(organization.fulflld_app_secret_last4),
                    },
                    {
                      label: 'Token status',
                      value: !organization.fulflld_enabled
                        ? <Tag>N/A</Tag>
                        : (organization.has_valid_fulflld_token && !organization.fulflld_token_fetch_failed
                          ? <Tag color="success">Valid</Tag>
                          : <Tag color="error">Invalid</Tag>),
                    },
                  ]}
                />
              </Card>
            </>
          )}
        </Space>
      ),
    },
    {
      key: 'email',
      label: 'Email',
      children: (
        <Space direction="vertical" size={sectionStackGap} style={{ width: '100%', maxWidth: settingsContentMaxWidth }}>
          <div>
            <Text style={{ color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.5 }}>
              Configure your domain to send emails from your own address (e.g. orders@yourdomain.com).
              Recipients see your brand and can reply directly.
            </Text>
          </div>

          {emailSendingLoading && <Spin />}

          {!emailSendingLoading && (
            <>
              <Card
                {...sectionCardProps}
                title={<SectionTitle title="Sending identity" tag={emailConn.tag} />}
              >
                <Text style={{
                  color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
                }}
                >
                  {emailConn.detail}
                </Text>
                <InfoTable
                  rows={[
                    {
                      label: 'Status',
                      value: emailConn.statusLabel,
                    },
                    {
                      label: 'From email',
                      value: emailConn.fromEmail
                        ? <Text code>{emailConn.fromEmail}</Text>
                        : '—',
                    },
                    {
                      label: 'From name',
                      value: emailConn.fromName || '—',
                    },
                    {
                      label: 'Sending domain',
                      value: emailConn.sendingDomain
                        ? <Text code>{emailConn.sendingDomain}</Text>
                        : '—',
                    },
                  ]}
                />
              </Card>

              <Card {...sectionCardProps} title="Domain authentication">
                <Text style={{
                  color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
                }}
                >
                  Enter your domain and desired from address. You will receive DNS records to add.
                </Text>
                <Form
                  className="org-settings-form"
                  form={emailForm}
                  layout="vertical"
                  onFinish={handleSaveEmailSending}
                  style={{ maxWidth: 480 }}
                >
                  <Form.Item
                    label="Sending Domain"
                    name="sending_domain"
                    rules={[{ required: true, message: 'Domain is required' }]}
                    extra="e.g. acme.com (no subdomain)"
                    style={formItemStyle}
                  >
                    <Input placeholder="acme.com" />
                  </Form.Item>

                  <Form.Item
                    label="From Email"
                    name="from_email"
                    rules={[{ required: true, message: 'From email is required' }]}
                    extra="e.g. orders@acme.com (must be on the domain above)"
                    style={formItemStyle}
                  >
                    <Input placeholder="orders@acme.com" />
                  </Form.Item>

                  <Form.Item
                    label="From Name"
                    name="from_name"
                    extra="Display name recipients see (e.g. Acme Catering)"
                    style={formItemStyle}
                  >
                    <Input placeholder="Acme Catering" />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="primary" htmlType="submit" loading={emailSendingSaving}>
                      Create Domain Authentication
                    </Button>
                  </Form.Item>
                </Form>
              </Card>

              {emailSending?.dns_records?.length > 0 && (
                <Card
                  {...sectionCardProps}
                  title={<SectionTitle title="DNS records" tag={emailConn.tag} />}
                >
                  <Text style={{
                    color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.4, marginBottom: 16,
                  }}
                  >
                    Add these records to your domain DNS, then click Verify.
                    Propagation usually takes under 1 hour (up to 48 hours).
                  </Text>
                  <Table
                    size="small"
                    dataSource={emailSending.dns_records}
                    columns={[
                      { title: 'Type', dataIndex: 'type', key: 'type', width: 80 },
                      {
                        title: 'Host',
                        dataIndex: 'host',
                        key: 'host',
                        render: (v) => <Text code copyable>{v}</Text>,
                      },
                      {
                        title: 'Value',
                        dataIndex: 'value',
                        key: 'value',
                        render: (v) => <Text code copyable>{v}</Text>,
                      },
                    ]}
                    rowKey={(r, i) => `${r.host}-${i}`}
                    pagination={false}
                    style={{ marginBottom: 16 }}
                  />
                  <Button
                    type="primary"
                    onClick={handleVerifyEmailSending}
                    loading={emailVerifyLoading}
                  >
                    Verify Domain
                  </Button>
                </Card>
              )}
            </>
          )}
        </Space>
      ),
    },
    {
      key: 'integrations',
      label: 'Integrations',
      children: (
        <Space direction="vertical" size={sectionStackGap} style={{ width: '100%', maxWidth: settingsContentMaxWidth }}>
          <div>
            <Text style={{ color: 'rgba(0, 0, 0, 0.75)', display: 'block', lineHeight: 1.5 }}>
              Connect external order sources and POS apps available to restaurants in this organization.
            </Text>
          </div>
          <EzCaterIntegration />
          {organization?.pos_integration_ui_enabled && <POSIntegrations />}
        </Space>
      ),
    },
  ];

  const tabBarItems = items.map(({ key, label }) => ({ key, label }));
  const activeContent = items.find((item) => item.key === activeTab)?.children;

  return (
    <Layout
      header={(
        <Layout.Header title="Settings" />
      )}
      subheader={(
        <Tabs
          className="org-settings-tabs"
          items={tabBarItems}
          activeKey={activeTab}
          onChange={onTabChange}
        />
      )}
    >
      {activeContent}
    </Layout>
  );
}

export default OrganizationSettings;
